import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminPagination from "@/components/admin/AdminPagination";
import AdminLayout from "../../../../components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import { AdminSearchForm } from "@/components/admin/AdminTableToolbar";
import {
  markDocumentSyncLogFailedReviewed,
  searchDocumentSyncLogs,
  type DocumentSyncLogFailureItem,
  type DocumentSyncLogFileItem,
  type DocumentSyncLogItem,
} from "../../../../services/documentSyncLog";
import { useAuth } from "../../../../context/auth";
import { ADMIN_ROLE, userHasAnyRole } from "../../../../utils/user";
import { getTeamsChannelName } from "@/utils/teamsChannelUrl";

// ─── 类型定义 ────────────────────────────────────────────────────────────────
type SyncStatus = "idle" | "syncing" | "done";
const PAGE_SIZE = 20;
const AUTO_REFRESH_INTERVAL_MS = 5_000;
const COLLAPSED_SOURCE_SITE_ROWS = 3;
const SOURCE_SITE_ROW_TOLERANCE_PX = 1;

type FileOperationTag = "new" | "modified" | "deleted" | "retry";

interface FileResult {
  file: string;
  reason: string;
  markKey?: string;
  tag?: FileOperationTag;
  path?: string;
}

// 成功文件行的数据形状(逐行携带 path / tag,避免按文件名映射导致同名串台)
interface SuccessFileItem {
  name: string;
  path?: string;
  tag?: FileOperationTag;
}

interface UpdateLog {
  id: string;
  datetime: string;
  scannedCount: number;
  addedCount: number;
  retriedCount: number;
  failedCount: number;
  updatedCount: number;
  skippedCount: number;
  deletedCount: number;
  dataSources: string[];
  sourceErrors: string[];
  failedDetails: DocumentSyncLogFailureItem[];
  // retry 后再次失败的明细(Suppressed)：不展示、不计数，仅用于把这些文件从 Succeeded 中剔除。
  suppressedFailedDetails: DocumentSyncLogFailureItem[];
  addedDetails: DocumentSyncLogFileItem[];
  retriedDetails: DocumentSyncLogFileItem[];
  updatedDetails: DocumentSyncLogFileItem[];
  deletedDetails: DocumentSyncLogFileItem[];
}

// ─── 后端字段适配 helpers ────────────────────────────────────────────────────
function formatDateTime(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const pad = (n: number) => String(n).padStart(2, "0");
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return { date, time };
}

function normalizeDataSources(dataSource: DocumentSyncLogItem["DataSource"]) {
  const values = Array.isArray(dataSource) ? dataSource : dataSource?.split(/[，,]/);
  return (values ?? []).map((value) => value.trim()).filter(Boolean);
}

// SourceError 后端返回 string[]（旧数据/异常时可能是整段纯文本字符串），统一成去空的字符串数组
function normalizeSourceErrors(sourceError: DocumentSyncLogItem["SourceError"]) {
  const values = Array.isArray(sourceError) ? sourceError : sourceError ? [sourceError] : [];
  return values.map((value) => value.trim()).filter(Boolean);
}

function normalizeLog(item: DocumentSyncLogItem): UpdateLog {
  return {
    id: item.ID,
    datetime: item.SyncTime,
    scannedCount: parseFloat(item.ScannedCount) || 0,
    addedCount: parseFloat(item.AddedCount) || 0,
    retriedCount: parseFloat(item.RetriedCount) || 0,
    failedCount: parseFloat(item.FailedCount) || 0,
    updatedCount: parseFloat(item.UpdatedCount) || 0,
    skippedCount: parseFloat(item.SkippedCount) || 0,
    deletedCount: parseFloat(item.DeletedCount) || 0,
    dataSources: normalizeDataSources(item.DataSource),
    sourceErrors: normalizeSourceErrors(item.SourceError),
    // Suppressed=true 的条目不展示为失败、不计数，仅用于从 Succeeded 中剔除（见 suppressedFailedDetails）。
    failedDetails: (item.FailedDetails ?? []).filter((f) => !f.Suppressed),
    suppressedFailedDetails: (item.FailedDetails ?? []).filter((f) => f.Suppressed),
    addedDetails: item.AddedDetails ?? [],
    retriedDetails: item.RetriedDetails ?? [],
    updatedDetails: item.UpdatedDetails ?? [],
    deletedDetails: item.DeletedDetails ?? [],
  };
}

// 后端目前未返回数据源对应的 SharePoint URL,这里基于站点名构造(未使用接口数据)
function buildSharePointUrl(name: string) {
  return `https://astrazeneca.sharepoint.com/sites/${encodeURIComponent(name)}`;
}

async function copyTextToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Clipboard API 可能因浏览器权限或非安全上下文失败，继续使用兼容方案。
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

// 每个 Copy 按钮独立维护反馈状态，避免同名数据源的多行按钮一起变成 Copied。
export function SourceSiteCopyButton({
  sourceSite,
  sourceUrl,
}: {
  sourceSite: string;
  sourceUrl?: string;
}) {
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">("idle");
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    },
    [],
  );

  const title =
    copyStatus === "copied"
      ? "Copied!"
      : copyStatus === "failed"
        ? "Copy failed"
        : sourceUrl
          ? "Copy Teams Channel link"
          : "Copy SharePoint link";

  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-200 transition-colors cursor-pointer"
      onClick={async (e) => {
        e.stopPropagation();
        // Teams 日志直接保存 Channel URL；SharePoint 日志仍由站点名拼接 URL。
        const copied = await copyTextToClipboard(sourceUrl || buildSharePointUrl(sourceSite));
        setCopyStatus(copied ? "copied" : "failed");
        if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
        resetTimerRef.current = setTimeout(() => {
          setCopyStatus("idle");
          resetTimerRef.current = null;
        }, 1500);
      }}
    >
      <i
        className={`text-[10px] ${
          copyStatus === "copied"
            ? "ri-check-line text-green-600"
            : copyStatus === "failed"
              ? "ri-error-warning-line text-red-500"
              : "ri-file-copy-line text-gray-500"
        }`}
      ></i>
    </button>
  );
}

export function UpdateLogDataSource({ dataSource }: { dataSource: string }) {
  const channelName = getTeamsChannelName(dataSource);

  return (
    <span
      data-source-site={dataSource}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 text-xs text-gray-600 border border-gray-200"
    >
      <span className="truncate max-w-[140px]" title={dataSource}>
        {channelName || dataSource}
      </span>
      <SourceSiteCopyButton
        sourceSite={dataSource}
        sourceUrl={channelName ? dataSource : undefined}
      />
    </span>
  );
}

function getVisibleSourceSiteCount(rowOffsets: number[], maxRows: number) {
  const rowTops: number[] = [];
  let visibleCount = 0;

  for (const offset of rowOffsets) {
    let rowIndex = rowTops.findIndex(
      (rowTop) => Math.abs(rowTop - offset) <= SOURCE_SITE_ROW_TOLERANCE_PX,
    );
    if (rowIndex === -1) {
      rowTops.push(offset);
      rowIndex = rowTops.length - 1;
    }
    if (rowIndex < maxRows) visibleCount += 1;
  }

  return visibleCount;
}

export function UpdateLogDataSources({ dataSources }: { dataSources: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = wrapRef.current;
    if (!container) return;

    const measure = () => {
      const containerTop = container.getBoundingClientRect().top;
      const items = Array.from(container.children) as HTMLElement[];
      const rowOffsets = items.map((item) => item.getBoundingClientRect().top - containerTop);
      const visibleCount = getVisibleSourceSiteCount(rowOffsets, COLLAPSED_SOURCE_SITE_ROWS);
      const nextHiddenCount = Math.max(0, items.length - visibleCount);
      const visibleItems = items.slice(0, visibleCount);
      const nextCollapsedHeight =
        nextHiddenCount > 0 && visibleItems.length > 0
          ? Math.ceil(
              Math.max(
                ...visibleItems.map((item) => item.getBoundingClientRect().bottom - containerTop),
              ),
            )
          : null;

      setHiddenCount((current) => (current === nextHiddenCount ? current : nextHiddenCount));
      setCollapsedHeight((current) =>
        current === nextCollapsedHeight ? current : nextCollapsedHeight,
      );
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [dataSources]);

  if (dataSources.length === 0) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  const showToggle = expanded || hiddenCount > 0;

  return (
    <div>
      <div
        ref={wrapRef}
        data-testid="update-log-data-sources-list"
        className="flex flex-wrap gap-1.5 content-start overflow-hidden transition-[max-height] duration-200"
        style={{
          maxHeight: expanded || collapsedHeight == null ? "none" : `${collapsedHeight}px`,
        }}
      >
        {dataSources.map((dataSource, index) => (
          <UpdateLogDataSource key={`${dataSource}-${index}`} dataSource={dataSource} />
        ))}
      </div>
      {showToggle && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((value) => !value);
          }}
          className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-50 text-xs text-gray-500 border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-xs`}></i>
          {expanded ? "Show less" : `+${hiddenCount} more`}
        </button>
      )}
    </div>
  );
}

// ───────────── Open Source(新窗口打开 SourcePath)─────────────
function OpenSourceButton({ path }: { path?: string }) {
  if (!path) return null;
  return (
    <a
      href={path}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="Open source"
      className="ml-auto w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-[#830051] hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
    >
      <i className="ri-external-link-line text-sm"></i>
    </a>
  );
}

function getFileTagLabel(tag?: FileOperationTag | string) {
  if (tag === "new" || tag === "New") return "New";
  if (tag === "modified" || tag === "Modified") return "Modified";
  if (tag === "deleted" || tag === "Deleted") return "Deleted";
  if (tag === "retry" || tag === "Retry") return "Retry";
  return "";
}

// 文件操作标记徽章。Retry(失败文件未关联本次任何 New/Modified/Deleted 操作,视为重试)用琥珀色区分
function FileTagBadge({
  tag,
  className = "",
}: {
  tag?: FileOperationTag | string;
  className?: string;
}) {
  const label = getFileTagLabel(tag);
  if (!label) return null;
  const color = label === "Retry" ? "bg-amber-50 text-amber-600" : "bg-gray-100 text-gray-500";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${color} ${className}`}
    >
      {label}
    </span>
  );
}

// ───────────── Source File Row(文件行 + 逐行 tag + SourcePath 跳转)─────────────
export function SourceFileRow({
  index,
  name,
  path,
  tag,
  textClassName = "text-gray-700",
}: {
  index: number;
  name: string;
  path?: string;
  tag?: FileOperationTag | string;
  textClassName?: string;
}) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-medium text-gray-400">
        {index + 1}
      </div>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className={`text-sm truncate min-w-0 ${textClassName}`} title={path || name}>
          {name}
        </span>
        <FileTagBadge tag={tag} className="flex-shrink-0" />
      </div>
      <OpenSourceButton path={path} />
    </div>
  );
}

// ───────────── Help Popover(字段说明,纯静态文案,未使用接口数据)─────────────
function HelpPopover() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        title="Field reference"
      >
        <i className="ri-question-line text-xs"></i>
      </button>

      {open && (
        <div className="absolute left-0 top-7 z-50 w-[460px] bg-white border border-gray-200 rounded-xl p-4 text-xs text-gray-600 leading-relaxed shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-800">
              {t("uploadLogs.helpTitle", "Source document scan results (from SharePoint)")}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer"
            >
              <i className="ri-close-line text-gray-400 text-xs"></i>
            </button>
          </div>

          {/* Source check */}
          <div className="mb-3">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0"></div>
              <span className="font-semibold text-gray-700">
                {t("uploadLogs.helpSourceHeader", "Source check (SharePoint)")}
              </span>
            </div>
            <table className="w-full font-sans">
              <tbody>
                {(
                  [
                    ["Scanned", "Number of files found and checked this time"],
                    [
                      "Skipped",
                      "Files already synced with no changes detected; system skipped processing",
                    ],
                    [
                      "New",
                      "Files with no existing record, newly submitted to processing workflow (files previously failed but resubmitted successfully are also included)",
                    ],
                    [
                      "Modified",
                      "Previously synced files whose content or update time changed in SharePoint, reprocessed this time",
                    ],
                    [
                      "Deleted",
                      "Files previously synced that were removed from SharePoint in this check",
                    ],
                  ] as [string, string][]
                ).map(([label, desc]) => (
                  <tr key={label} className="align-top">
                    <td className="pr-3 py-1 font-semibold text-gray-700 whitespace-nowrap w-[80px]">
                      {label}
                    </td>
                    <td className="py-1 text-gray-500 leading-snug">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0"></div>
              <span className="font-semibold text-gray-700">
                {t("uploadLogs.helpKBHeader", "Knowledge base sync result")}
              </span>
            </div>
            <table className="w-full font-sans">
              <tbody>
                {(
                  [
                    [
                      "Succeeded",
                      "Files successfully processed and added to the knowledge base this time",
                    ],
                    [
                      "Failed",
                      "Files that failed during sync or processing (reasons may include download failure, upload failure, or parsing failure; this number may continue to update as backend processing completes)",
                    ],
                  ] as [string, string][]
                ).map(([label, desc]) => (
                  <tr key={label} className="align-top">
                    <td className="pr-3 py-1 font-semibold text-gray-700 whitespace-nowrap w-[80px]">
                      {label}
                    </td>
                    <td className="py-1 text-gray-500 leading-snug">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────── Review Help Popover(未使用接口数据)─────────────
function ReviewHelpPopover() {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (btnRef.current && !btnRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleClick = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const tooltipWidth = 288;
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      left = Math.min(left, window.innerWidth - tooltipWidth - 16);
      left = Math.max(left, 16);
      setPos({ top: rect.bottom + 8, left });
    }
    setOpen((v) => !v);
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleClick}
        className="w-5 h-5 flex items-center justify-center text-[#830051] hover:text-[#9a1a62] transition-colors cursor-pointer"
        title="About mark as reviewed"
      >
        <i className="ri-question-line text-xs"></i>
      </button>
      {open && (
        <div
          className="fixed z-[9999] w-72 bg-white border border-gray-200 rounded-lg p-3 text-xs text-gray-600 leading-relaxed shadow-lg"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-800">Mark as Reviewed</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="w-5 h-5 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer"
            >
              <i className="ri-close-line text-gray-400 text-xs"></i>
            </button>
          </div>
          <p className="text-gray-500">
            Click the check box next to a failed file to mark it as reviewed. This helps you track
            which failures have been acknowledged and does not affect the actual sync status. Click
            again to unmark.
          </p>
        </div>
      )}
    </>
  );
}

// ───────────── File List Modal ─────────────
function FileListModal({
  open,
  onClose,
  title,
  files,
  failedFiles,
  fileItems,
  markedItems,
  onToggleMark,
  onUnmark,
  canMarkReviewed = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  files: string[];
  failedFiles?: FileResult[];
  // 携带逐行 tag / path 的成功文件列表;按文件名映射会在同名文件跨 New/Modified/Deleted 时串台,故改为逐行传递
  fileItems?: SuccessFileItem[];
  markedItems?: Set<string>;
  onToggleMark?: (key: string) => void;
  onUnmark?: (key: string) => void;
  canMarkReviewed?: boolean;
}) {
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    const previous = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  const isFailed = failedFiles != null && failedFiles.length > 0;
  const successItems: SuccessFileItem[] = fileItems ?? files.map((f) => ({ name: f }));
  const allItems = isFailed
    ? failedFiles!.map((f) => ({
        name: f.file,
        reason: f.reason,
        markKey: f.markKey,
        tag: f.tag as string | undefined,
        path: f.path,
      }))
    : successItems.map((f) => ({
        name: f.name,
        reason: "",
        markKey: undefined as string | undefined,
        tag: f.tag as string | undefined,
        path: f.path,
      }));

  const filtered = allItems.filter((item) => item.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ marginTop: 0 }}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>
        <div className="px-5 py-3 border-b border-gray-100">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-gray-400 text-xs"></i>
            </div>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search files..."
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-300"
            />
          </div>
        </div>
        <div className="overflow-y-auto flex-1 p-0">
          <div className="divide-y divide-gray-100">
            {filtered.map((item, idx) => {
              const isMarked = item.markKey ? markedItems?.has(item.markKey) : false;
              return (
                <div key={idx} className={`px-5 py-2.5 ${isMarked ? "opacity-50" : ""}`}>
                  <div className="flex items-start gap-3">
                    {isFailed && item.markKey && canMarkReviewed && (
                      <button
                        type="button"
                        onClick={() => {
                          if (isMarked) onUnmark?.(item.markKey!);
                          else onToggleMark?.(item.markKey!);
                        }}
                        className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0 mt-0.5"
                      >
                        <i
                          className={`${
                            isMarked
                              ? "ri-checkbox-fill text-[#830051]"
                              : "ri-checkbox-blank-line text-[#830051]"
                          } text-base`}
                        ></i>
                      </button>
                    )}
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px] font-medium text-gray-400">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm break-all ${isMarked ? "text-gray-400" : "text-gray-700"}`}
                      >
                        {item.name}
                        <FileTagBadge tag={item.tag} className="ml-2" />
                      </p>
                      {item.reason && (
                        <p
                          className={`text-xs mt-0.5 leading-snug ${isMarked ? "text-gray-400" : "text-red-500"}`}
                        >
                          {item.reason}
                        </p>
                      )}
                    </div>
                    <OpenSourceButton path={item.path} />
                  </div>
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && (
            <div className="px-5 py-10 text-center">
              {allItems.length === 0 ? (
                <>
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-gray-50">
                    <i className="ri-folder-open-line text-gray-300 text-lg"></i>
                  </div>
                  <p className="text-sm text-gray-400">No detailed file list available</p>
                </>
              ) : (
                <p className="text-sm text-gray-400">No results</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ───────────── Stat Badge ─────────────
function StatBadge({
  label,
  value,
  expandable,
  expanded,
  onToggle,
  title,
}: {
  label: string;
  value: number;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  title?: string;
}) {
  const baseClasses =
    "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border whitespace-nowrap transition-colors bg-gray-50 text-gray-500 border-gray-200";

  const badge = (
    <button
      type="button"
      onClick={expandable ? onToggle : undefined}
      className={`${baseClasses} ${expandable ? "cursor-pointer hover:bg-gray-100" : "cursor-default"}`}
      disabled={!expandable}
    >
      {label} {value}
      {expandable && (
        <i
          className={`ri-arrow-down-s-line text-xs transition-transform ${expanded ? "rotate-180" : ""}`}
        ></i>
      )}
    </button>
  );

  if (!title) return badge;

  return (
    <div className="relative inline-flex group">
      {badge}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-800 text-white text-[11px] rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 max-w-xs">
        <span className="whitespace-normal block">{title}</span>
      </div>
    </div>
  );
}

// ───────────── KB Sync Summary Bar ─────────────
function KBSyncBar({
  addedCount,
  failedCount,
  addedExpanded,
  failedExpanded,
  onToggleAdded,
  onToggleFailed,
  hideSuccess,
  t,
}: {
  addedCount: number;
  failedCount: number;
  addedExpanded?: boolean;
  failedExpanded?: boolean;
  onToggleAdded?: () => void;
  onToggleFailed?: () => void;
  hideSuccess?: boolean;
  t: (key: string, fallback?: string) => string;
}) {
  const showSuccess = !hideSuccess && addedCount > 0;
  const showFailed = failedCount > 0;
  if (!showSuccess && !showFailed) return null;

  return (
    <div className="flex items-center gap-3 mt-3 px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-200">
      <span className="text-[11px] text-gray-500 whitespace-nowrap font-medium">
        {t("uploadLogs.kbSyncResult", "Knowledge Base Sync Result")}:
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        {showSuccess && (
          <button
            type="button"
            onClick={onToggleAdded}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200 whitespace-nowrap cursor-pointer hover:bg-green-100 transition-colors"
          >
            {addedCount} {t("uploadLogs.addedCount", "Succeeded")}
            <i
              className={`ri-arrow-down-s-line text-[10px] transition-transform ${addedExpanded ? "rotate-180" : ""}`}
            ></i>
          </button>
        )}
        {showFailed && (
          <button
            type="button"
            onClick={onToggleFailed}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 border border-red-200 whitespace-nowrap cursor-pointer hover:bg-red-100 transition-colors"
          >
            {failedCount} {t("uploadLogs.failedCount", "Failed")}
            <i
              className={`ri-arrow-down-s-line text-[10px] transition-transform ${failedExpanded ? "rotate-180" : ""}`}
            ></i>
          </button>
        )}
      </div>
    </div>
  );
}

// ───────────── Operation Panel ─────────────
function OperationPanel({
  title,
  count,
  successCount,
  failedCount,
  successFiles,
  failedFiles,
  theme,
  t,
  onViewAllSuccess,
  onViewAllFailed,
  markKeyPrefix,
  markedItems,
  onToggleMark,
  onUnmark,
  canMarkReviewed,
  hideSuccess,
}: {
  title: string;
  count: number;
  successCount: number;
  failedCount: number;
  successFiles: SuccessFileItem[];
  failedFiles: FileResult[];
  theme: "pink" | "slate";
  t: (key: string, fallback?: string) => string;
  onViewAllSuccess?: () => void;
  onViewAllFailed?: () => void;
  markKeyPrefix: string;
  markedItems: Set<string>;
  onToggleMark: (key: string) => void;
  onUnmark: (key: string) => void;
  canMarkReviewed: boolean;
  hideSuccess?: boolean;
}) {
  const isPink = theme === "pink";
  const bg = "#fdf2f8";
  const headerBg = "#fce7f3";
  const border = "#fbcfe8";
  const accent = "#830051";
  const iconClass = isPink ? "ri-file-list-3-line" : "ri-refresh-line";
  const previewLimit = 5;

  return (
    <div
      className="mt-3 rounded-lg border overflow-hidden"
      style={{ backgroundColor: bg, borderColor: border }}
    >
      <div
        className="flex items-center justify-between px-4 py-2.5 border-b"
        style={{ backgroundColor: headerBg, borderColor: border }}
      >
        <div className="flex items-center gap-2">
          <i className={`${iconClass} text-sm`} style={{ color: accent }}></i>
          <span className="text-xs font-semibold" style={{ color: accent }}>
            {title}
          </span>
        </div>
        <span className="text-xs font-medium" style={{ color: accent }}>
          {count} {t("uploadLogs.files", "files")}
        </span>
      </div>

      {/* Succeeded */}
      {!hideSuccess && successCount > 0 && (
        <div
          className="px-4 py-2 border-b"
          style={{ borderColor: border, backgroundColor: "#ffffff" }}
        >
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs font-medium text-green-700">
              {t("uploadLogs.succeeded")} ({successCount})
            </div>
            {successCount > previewLimit && onViewAllSuccess && (
              <button
                type="button"
                onClick={onViewAllSuccess}
                className="text-xs font-medium text-[#830051] hover:underline whitespace-nowrap cursor-pointer"
              >
                {t("uploadLogs.viewAll")} {successCount}
              </button>
            )}
          </div>
          {successFiles.length > 0 ? (
            <div className="divide-y divide-green-100">
              {successFiles.slice(0, previewLimit).map((file, idx) => (
                <SourceFileRow key={idx} index={idx} name={file.name} path={file.path} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              {successCount} {t("uploadLogs.files")} added to knowledge base
            </p>
          )}
        </div>
      )}

      {/* Failed */}
      {failedCount > 0 && (
        <div className="px-4 py-2" style={{ backgroundColor: "#fef2f2" }}>
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <div className="text-xs font-medium text-red-700">
                {t("uploadLogs.failedFiles")} ({failedCount})
              </div>
              {canMarkReviewed && <ReviewHelpPopover />}
            </div>
            {failedCount > previewLimit && onViewAllFailed && (
              <button
                type="button"
                onClick={onViewAllFailed}
                className="text-xs font-medium text-[#830051] hover:underline whitespace-nowrap cursor-pointer"
              >
                {t("uploadLogs.viewAll")} {failedCount}
              </button>
            )}
          </div>
          <div className="divide-y divide-red-100">
            {failedFiles.slice(0, previewLimit).map((item, idx) => {
              const key = item.markKey ?? `${markKeyPrefix}-${item.file}`;
              const isMarked = markedItems.has(key);
              return (
                <div
                  key={idx}
                  className={`flex items-start gap-3 py-1.5 ${isMarked ? "opacity-50" : ""}`}
                >
                  <div className="flex items-start gap-3 flex-shrink-0">
                    {canMarkReviewed && (
                      <button
                        type="button"
                        onClick={() => (isMarked ? onUnmark(key) : onToggleMark(key))}
                        className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                      >
                        <i
                          className={`${
                            isMarked
                              ? "ri-checkbox-fill text-[#830051]"
                              : "ri-checkbox-blank-line text-[#830051]"
                          } text-base`}
                        ></i>
                      </button>
                    )}
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-medium text-gray-400">
                      {idx + 1}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm ${isMarked ? "text-gray-400" : "text-gray-700"}`}>
                      {item.file}
                    </span>
                    <p
                      className={`text-xs mt-0.5 leading-snug ${isMarked ? "text-gray-400" : "text-red-500"}`}
                    >
                      {item.reason}
                    </p>
                  </div>
                  <OpenSourceButton path={item.path} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────── Status Dropdown(纯前端过滤,未使用接口数据)─────────────
type FilterStatus = "all" | "hasAdded" | "hasFailures";
type FilterFailedSub = "all" | "unreviewed" | "reviewed";

function StatusDropdown({
  typeValue,
  reasonValue,
  onTypeChange,
  onReasonChange,
  t,
}: {
  typeValue: FilterStatus;
  reasonValue: FilterFailedSub;
  onTypeChange: (v: FilterStatus) => void;
  onReasonChange: (v: FilterFailedSub) => void;
  t: (key: string, fallback?: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSubOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const typeOptions: { value: FilterStatus; label: string }[] = [
    { value: "all", label: t("uploadLogs.filterAll", "All Type") },
    { value: "hasAdded", label: t("uploadLogs.filterHasAdded", "Succeeded") },
    { value: "hasFailures", label: t("uploadLogs.filterHasFailures", "Failed") },
  ];

  const reasonOptions: { value: FilterFailedSub; label: string }[] = [
    { value: "all", label: t("uploadLogs.failedFilterAll", "All") },
    { value: "unreviewed", label: t("uploadLogs.failedFilterUnreviewed", "Unreviewed") },
    { value: "reviewed", label: t("uploadLogs.failedFilterReviewed", "Reviewed") },
  ];

  const selectedType = typeOptions.find((o) => o.value === typeValue)!;
  const selectedReason = reasonOptions.find((o) => o.value === reasonValue)!;

  const triggerLabel =
    typeValue === "hasFailures" && reasonValue !== "all"
      ? `${selectedType.label} · ${selectedReason.label}`
      : selectedType.label;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setSubOpen(false);
        }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
      >
        <span className="text-gray-700">{triggerLabel}</span>
        <i
          className={`ri-arrow-down-s-line text-gray-400 text-xs transition-transform ${open ? "rotate-180" : ""}`}
        ></i>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg py-1 min-w-[130px] shadow-md">
          {typeOptions.map((opt) => {
            const isFailures = opt.value === "hasFailures";
            const isActive = typeValue === opt.value;
            return (
              <div
                key={opt.value}
                className="relative"
                onMouseEnter={() => {
                  if (isFailures) setSubOpen(true);
                  else setSubOpen(false);
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (isFailures) {
                      onTypeChange("hasFailures");
                      setSubOpen((v) => !v);
                    } else {
                      onTypeChange(opt.value);
                      onReasonChange("all");
                      setOpen(false);
                      setSubOpen(false);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isActive ? "text-[#830051] bg-pink-50" : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span>{opt.label}</span>
                  {isFailures && (
                    <i className="ri-arrow-right-s-line text-gray-400 text-xs ml-3"></i>
                  )}
                </button>

                {isFailures && subOpen && (
                  <div className="absolute left-full top-0 ml-0.5 z-50 bg-white border border-gray-200 rounded-lg py-1 min-w-[130px] shadow-md">
                    {reasonOptions.map((ro) => (
                      <button
                        key={ro.value}
                        type="button"
                        onClick={() => {
                          onTypeChange("hasFailures");
                          onReasonChange(ro.value);
                          setOpen(false);
                          setSubOpen(false);
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          typeValue === "hasFailures" && reasonValue === ro.value
                            ? "text-[#830051] bg-pink-50"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {ro.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function UpdateLogsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const canMarkReviewed = userHasAnyRole(user?.roles, [ADMIN_ROLE]);
  // 子组件需要 (key, fallback) => string 形式,这里包装一下 i18next 的 t
  const tr = useCallback(
    (key: string, fallback?: string) =>
      fallback != null ? (t(key, { defaultValue: fallback }) as string) : (t(key) as string),
    [t],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [filterFailedSub, setFilterFailedSub] = useState<FilterFailedSub>("all");
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [logs, setLogs] = useState<UpdateLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  // 行手风琴:每条 log 同一时刻只展开一个 panel(对齐 UpdateLogPage.ts)
  const [expandedPanel, setExpandedPanel] = useState<Record<string, string>>({});
  // failed 文件 mark 状态(纯前端,未持久化到后端)
  const [markedItems, setMarkedItems] = useState<Set<string>>(new Set());
  // 全局弹窗
  const [modalState, setModalState] = useState<{
    open: boolean;
    title: string;
    files: string[];
    failedFiles?: FileResult[];
    fileItems?: { name: string; tag?: FileOperationTag }[];
  }>({ open: false, title: "", files: [] });

  const openFileModal = (params: {
    title: string;
    files: string[];
    failedFiles?: FileResult[];
    fileItems?: { name: string; tag?: FileOperationTag }[];
  }) => setModalState({ open: true, ...params });
  const closeFileModal = () => setModalState((prev) => ({ ...prev, open: false }));

  const togglePanel = (id: string, panel: string) => {
    setExpandedPanel((prev) => {
      if (prev[id] === panel) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: panel };
    });
  };

  // markKey → 后端定位标识，用于持久化 reviewed 状态时调用 API。
  const markKeyLookup = useMemo(() => {
    const map = new Map<
      string,
      { logId: string; documentGuid?: string; srcDocumentId?: string; fileName?: string }
    >();
    for (const log of logs) {
      for (const f of log.failedDetails) {
        const key = `${log.id}-failed-${f.DocumentGuid ?? f.SrcDocumentId ?? f.FileName ?? ""}`;
        map.set(key, {
          logId: log.id,
          documentGuid: f.DocumentGuid ?? undefined,
          srcDocumentId: f.SrcDocumentId ?? undefined,
          fileName: f.FileName ?? undefined,
        });
      }
    }
    return map;
  }, [logs]);

  const persistReviewed = useCallback(
    async (key: string, isReviewed: boolean) => {
      if (!canMarkReviewed) return;
      const info = markKeyLookup.get(key);
      if (!info) return;
      try {
        await markDocumentSyncLogFailedReviewed({
          LogId: info.logId,
          DocumentGuid: info.documentGuid,
          SrcDocumentId: info.srcDocumentId,
          FileName: info.fileName,
          IsReviewed: isReviewed,
        });
      } catch {
        // 回滚乐观更新
        setMarkedItems((prev) => {
          const next = new Set(prev);
          if (isReviewed) next.delete(key);
          else next.add(key);
          return next;
        });
      }
    },
    [canMarkReviewed, markKeyLookup],
  );

  const handleToggleMark = useCallback(
    (key: string) => {
      setMarkedItems((prev) => new Set([...prev, key]));
      void persistReviewed(key, true);
    },
    [persistReviewed],
  );
  const handleUnmark = useCallback(
    (key: string) => {
      setMarkedItems((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      void persistReviewed(key, false);
    },
    [persistReviewed],
  );

  const fetchLogs = useCallback(
    async (showLoading = true) => {
      if (showLoading) {
        setLoading(true);
        setLoadError("");
      }
      try {
        const res = await searchDocumentSyncLogs({
          Keyword: searchQuery.trim() || undefined,
          Status:
            filterStatus === "hasAdded"
              ? "succeeded"
              : filterStatus === "hasFailures"
                ? "failed"
                : undefined,
          FailedReviewStatus:
            filterStatus === "hasFailures" && filterFailedSub !== "all"
              ? filterFailedSub
              : undefined,
          Page: page,
          Limit: PAGE_SIZE,
        });
        const list = (res.data.Data ?? []).map(normalizeLog);
        setLoadError("");
        setLogs(list);
        setTotal(res.data.Count ?? list.length);
        setPage(res.data.Page ?? page);
        // 根据服务端返回的 IsReviewed 重建本地勾选状态。
        const nextMarked = new Set<string>();
        for (const log of list) {
          for (const f of log.failedDetails) {
            if (f.IsReviewed) {
              nextMarked.add(
                `${log.id}-failed-${f.DocumentGuid ?? f.SrcDocumentId ?? f.FileName ?? ""}`,
              );
            }
          }
        }
        setMarkedItems(nextMarked);
      } catch {
        if (showLoading) {
          setLoadError(t("updateLogs.loadFailed"));
          setLogs([]);
          setTotal(0);
        }
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [page, searchQuery, filterStatus, filterFailedSub, t],
  );

  useEffect(() => {
    void fetchLogs();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") void fetchLogs(false);
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [fetchLogs]);

  // 保留手动同步逻辑(原 UI 中按钮已注释隐藏)
  const handleSync = () => {
    if (syncStatus === "syncing") return;
    setSyncStatus("syncing");
    setTimeout(() => {
      setSyncStatus("done");
      const locale = i18n.language.startsWith("zh") ? "zh-CN" : "en-US";
      setLastSyncTime(
        new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }),
      );
      setTimeout(() => setSyncStatus("idle"), 3000);
      void fetchLogs();
    }, 2000);
  };
  void handleSync;

  // 关键词 / Status / FailedReviewStatus 由后端过滤，前端直接使用 logs。

  return (
    <AdminLayout>
      <div>
        {/* Header(保留:title / subtitle / 同步状态 — 接口数据)*/}
        <div className="flex items-start justify-between mb-6">
          <div>
            <AdminPageTitle title={t("updateLogs.title")} tooltip={t("updateLogs.subtitle")} />
          </div>
          <div className="flex items-center gap-3">
            {syncStatus === "done" && lastSyncTime && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <i className="ri-check-line"></i>
                {t("updateLogs.syncedAt", { time: lastSyncTime })}
              </span>
            )}
            {lastSyncTime && syncStatus !== "done" && (
              <span className="text-xs text-gray-400">
                {t("updateLogs.lastSynced", { time: lastSyncTime })}
              </span>
            )}
          </div>
        </div>

        {/* Search / Filter 一体化条 */}
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <StatusDropdown
              typeValue={filterStatus}
              reasonValue={filterFailedSub}
              onTypeChange={(v) => {
                setFilterStatus(v);
                if (v !== "hasFailures") setFilterFailedSub("all");
                setPage(1);
              }}
              onReasonChange={(v) => {
                setFilterFailedSub(v);
                setPage(1);
              }}
              t={tr}
            />
          </div>
          <AdminSearchForm
            value={searchInput}
            onChange={setSearchInput}
            onSearch={() => {
              setSearchQuery(searchInput);
              setPage(1);
            }}
            searchLabel={t("common.search")}
            placeholder={t("updateLogs.searchPlaceholder")}
            showReset={
              searchQuery !== "" ||
              searchInput !== "" ||
              filterStatus !== "all" ||
              filterFailedSub !== "all"
            }
            onReset={() => {
              setSearchQuery("");
              setSearchInput("");
              setFilterStatus("all");
              setFilterFailedSub("all");
              setPage(1);
            }}
            resetLabel={t("common.reset")}
          />
          <div className="flex-1" />

          <span className="text-sm text-gray-400 whitespace-nowrap">
            {t("updateLogs.totalCount", { count: total || logs.length })}
          </span>
        </div>

        {loadError && (
          <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
            {loadError}
          </div>
        )}

        {/* Grid 表格(对齐 UpdateLogPage.ts:120 / 260 / 1fr)*/}
        <div className="bg-white rounded-xl border border-gray-100 overflow-visible">
          {/* Header */}
          <div className="grid grid-cols-[120px_260px_1fr] gap-4 px-6 py-3 border-b border-gray-100 bg-gray-50">
            <div className="text-xs font-semibold text-gray-500">{t("updateLogs.colDate")}</div>
            <div className="text-xs font-semibold text-gray-500">
              {t("updateLogs.colDataSource")}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500">
                {t("updateLogs.colDescription")}
              </span>
              <HelpPopover />
            </div>
          </div>

          {/* Rows */}
          {loading ? (
            <div className="px-6 py-16 text-center text-sm text-gray-400">
              <i className="ri-loader-4-line text-2xl animate-spin inline-block mb-2"></i>
              <div>{t("common.loading")}</div>
            </div>
          ) : logs.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-gray-400">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                <i className="ri-file-list-3-line text-3xl text-gray-300"></i>
              </div>
              {t("updateLogs.noRecords")}
            </div>
          ) : (
            logs.map((log, index) => {
              const { date, time } = formatDateTime(log.datetime);
              const isLast = index === logs.length - 1;

              // ── 数据派生(对齐 UploadLogs 语义)──
              // New/Retry/Modified/Deleted 徽章数字:
              //   - 当右上角筛选为 Failed 时 = 详情列表中文档身份出现在 FailedDetails 的文件数
              //   - 其他筛选时 = 本次操作数 - retry 再失败(suppressed)。suppressed 既不算成功也不算 New,
              //     否则徽章数字会比展开的 成功+失败 列表多出这些既不展示成功也不展示失败的文件。
              // isInFailed：仅展示失败(failedDetails)，用于 Failed 筛选下的徽章计数与失败列表归类。
              const getDocumentIdentity = (
                item: Pick<
                  DocumentSyncLogFileItem,
                  "DocumentGuid" | "SrcDocumentId" | "SourcePathAskId"
                >,
              ) =>
                item.DocumentGuid?.trim() ||
                (item.SourcePathAskId && item.SrcDocumentId
                  ? `${item.SourcePathAskId}:${item.SrcDocumentId}`
                  : item.SrcDocumentId?.trim()) ||
                "";
              const failedDocumentIds = new Set(
                log.failedDetails
                  .map(getDocumentIdentity)
                  .filter((id): id is string => Boolean(id)),
              );
              const isInFailed = (item: DocumentSyncLogFileItem) => {
                const id = getDocumentIdentity(item);
                return Boolean(id) && failedDocumentIds.has(id);
              };
              // isSuppressed：retry 再失败的文件。不展示为失败，也不算成功/New。
              const suppressedDocumentIds = new Set(
                log.suppressedFailedDetails
                  .map(getDocumentIdentity)
                  .filter((id): id is string => Boolean(id)),
              );
              const isSuppressed = (item: DocumentSyncLogFileItem) => {
                const id = getDocumentIdentity(item);
                return Boolean(id) && suppressedDocumentIds.has(id);
              };
              // isExcludedFromSuccess：展示失败 + retry 再失败(suppressed)。两类都不能出现在 Succeeded 里；
              // suppressed 不展示为失败，但仍要从 Succeeded 抹掉（失败不能被显示为成功）。
              const isExcludedFromSuccess = (item: DocumentSyncLogFileItem) =>
                isInFailed(item) || isSuppressed(item);
              const failedFilterActive = filterStatus === "hasFailures";
              const newCount = failedFilterActive
                ? log.addedDetails.filter(isInFailed).length
                : log.addedDetails.filter((f) => !isSuppressed(f)).length;
              const retryCount = failedFilterActive
                ? log.retriedDetails.filter(isInFailed).length
                : log.retriedDetails.filter((f) => !isSuppressed(f)).length;
              const modifiedCount = failedFilterActive
                ? log.updatedDetails.filter(isInFailed).length
                : log.updatedDetails.filter((f) => !isSuppressed(f)).length;
              const deletedCount = failedFilterActive
                ? log.deletedDetails.filter(isInFailed).length
                : log.deletedDetails.filter((f) => !isSuppressed(f)).length;

              const kbFailed = log.failedCount;

              // 每个展开面板内部需要把"已知失败的同一 DocumentGuid 条目"从 Succeeded 中剔除,
              // 避免同一文件既在 Succeeded 又在 Failed 里出现(场景:初次同步成功 → PIKE-RAG 异步处理失败)
              // 也剔除 retry 再失败(suppressed)的文件——它们不展示为失败，但同样不能算成功。
              // 逐行携带 FileName / SourcePath / tag,渲染时不再按文件名查表(同名文件会串台)
              const toSuccessItems = (
                details: DocumentSyncLogFileItem[],
                tag: FileOperationTag,
              ): SuccessFileItem[] =>
                details
                  .filter((f) => !isExcludedFromSuccess(f))
                  .filter((f) => Boolean(f.FileName))
                  .map((f) => ({ name: f.FileName!, path: f.SourcePath ?? undefined, tag }));
              const addedSuccessFiles = toSuccessItems(log.addedDetails, "new");
              const retriedSuccessFiles = toSuccessItems(log.retriedDetails, "retry");
              const updatedSuccessFiles = toSuccessItems(log.updatedDetails, "modified");
              const deletedSuccessFiles = toSuccessItems(log.deletedDetails, "deleted");

              // KB Sync 汇总:成功 = 去重后剔除最终失败文件的明细条数, 失败 = failedCount
              // 不能直接用 addedCount/updatedCount —— 这些文件初次同步成功后可能被 PIKE-RAG
              // 异步处理判失败,异步失败只追加 FailedDetails/FailedCount 而不回退 AddedCount,
              // 直接相加会把已失败文件重复计入,导致"Succeeded 12 / Failed 12 但展开为空"。
              // 注:后端无单独的 deletedFailed 字段,删除全部视为成功
              const kbSuccessItems = [
                ...addedSuccessFiles,
                ...retriedSuccessFiles,
                ...updatedSuccessFiles,
                ...deletedSuccessFiles,
              ];
              const kbAdded = kbSuccessItems.length;
              // 用 DocumentGuid（历史数据回退 SrcDocumentId）反查失败文件的操作类型与 SourcePath。
              // 同一源文件属于多个 Source/KB 时 DocumentGuid 不同，不会互相串台。
              const documentIdToTag = new Map<string, FileOperationTag>();
              const documentIdToPath = new Map<string, string>();
              for (const f of log.addedDetails) {
                const id = getDocumentIdentity(f);
                if (id) documentIdToTag.set(id, "new");
              }
              for (const f of log.retriedDetails) {
                const id = getDocumentIdentity(f);
                if (id) documentIdToTag.set(id, "retry");
              }
              for (const f of log.updatedDetails) {
                const id = getDocumentIdentity(f);
                if (id) documentIdToTag.set(id, "modified");
              }
              for (const f of log.deletedDetails) {
                const id = getDocumentIdentity(f);
                if (id) documentIdToTag.set(id, "deleted");
              }
              for (const f of [
                ...log.addedDetails,
                ...log.retriedDetails,
                ...log.updatedDetails,
                ...log.deletedDetails,
              ]) {
                const id = getDocumentIdentity(f);
                if (id && f.SourcePath) documentIdToPath.set(id, f.SourcePath);
              }
              const failedFileResults: FileResult[] = log.failedDetails.map((f) => {
                // 失败文件未关联本次任何明确操作时，按历史兼容规则视为 Retry。
                const documentIdentity = getDocumentIdentity(f);
                const matchedTag = documentIdentity
                  ? documentIdToTag.get(documentIdentity)
                  : undefined;
                return {
                  file: f.FileName ?? "",
                  reason: f.Reason ?? t("updateLogs.unknownFailureReason"),
                  markKey: `${log.id}-failed-${documentIdentity || f.FileName || ""}`,
                  tag: matchedTag ?? "retry",
                  path:
                    f.SourcePath ??
                    (documentIdentity ? documentIdToPath.get(documentIdentity) : undefined),
                };
              });

              // 把失败文件按操作类型分桶,各操作面板只展示属于自己的失败条目
              const newFailedFileResults = failedFileResults.filter((f) => f.tag === "new");
              const retryFailedFileResults = failedFileResults.filter((f) => f.tag === "retry");
              const modifiedFailedFileResults = failedFileResults.filter(
                (f) => f.tag === "modified",
              );
              const deletedFailedFileResults = failedFileResults.filter((f) => f.tag === "deleted");

              const newSuccess = addedSuccessFiles.length;
              const newFailed = newFailedFileResults.length;
              const newTotal = newSuccess + newFailed;
              const retrySuccess = retriedSuccessFiles.length;
              const retryFailed = retryFailedFileResults.length;
              const retryTotal = retrySuccess + retryFailed;
              const modifiedSuccess = updatedSuccessFiles.length;
              const modifiedFailed = modifiedFailedFileResults.length;
              const modifiedTotal = modifiedSuccess + modifiedFailed;
              const deletedSuccess = deletedSuccessFiles.length;
              const deletedFailed = deletedFailedFileResults.length;
              const deletedTotal = deletedSuccess + deletedFailed;
              const hasNew = newTotal > 0;
              const hasRetry = retryTotal > 0;
              const hasModified = modifiedTotal > 0;
              const hasDeleted = deletedTotal > 0;

              return (
                <div
                  key={log.id}
                  className={`grid grid-cols-[120px_260px_1fr] gap-4 px-6 py-4 items-start ${
                    !isLast ? "border-b border-gray-100" : ""
                  }`}
                >
                  {/* 日期 */}
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{date}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{time}</div>
                  </div>

                  {/* 数据源 */}
                  <div>
                    <UpdateLogDataSources dataSources={log.dataSources} />
                  </div>

                  {/* 描述列 */}
                  <div>
                    {/* 源端错误（预检失败 / 站点或子目录遍历异常）—— 显示在统计胶囊上一行 */}
                    {log.sourceErrors.length > 0 && (
                      <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                        <div className="flex items-center gap-1.5 mb-1">
                          <i className="ri-error-warning-line text-amber-600 text-sm"></i>
                          <span className="text-[11px] font-semibold text-amber-700">
                            {t("updateLogs.sourceError", "Source error")}
                          </span>
                        </div>
                        <ul className="space-y-0.5">
                          {log.sourceErrors.map((err, i) => (
                            <li key={i} className="text-xs text-amber-700 leading-snug break-all">
                              {err}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* 徽章组 */}
                    <div className="flex flex-wrap gap-2 items-center">
                      <StatBadge label={t("updateLogs.countScanned")} value={log.scannedCount} />
                      <div className="w-px h-4 bg-gray-300 mx-1"></div>
                      <StatBadge label={t("updateLogs.countSkipped")} value={log.skippedCount} />
                      <StatBadge
                        label={t("updateLogs.countAdded")}
                        value={newCount}
                        expandable={hasNew}
                        expanded={expandedPanel[log.id] === "new"}
                        onToggle={() => togglePanel(log.id, "new")}
                      />
                      <StatBadge
                        label={t("updateLogs.countRetried")}
                        value={retryCount}
                        expandable={hasRetry}
                        expanded={expandedPanel[log.id] === "retry"}
                        onToggle={() => togglePanel(log.id, "retry")}
                      />
                      <StatBadge
                        label={t("updateLogs.countUpdated")}
                        value={modifiedCount}
                        expandable={hasModified}
                        expanded={expandedPanel[log.id] === "modified"}
                        onToggle={() => togglePanel(log.id, "modified")}
                      />
                      <StatBadge
                        label={t("updateLogs.countDeleted")}
                        value={deletedCount}
                        expandable={hasDeleted}
                        expanded={expandedPanel[log.id] === "deleted"}
                        onToggle={() => togglePanel(log.id, "deleted")}
                      />
                    </div>

                    {/* KB Sync 汇总条 */}
                    <KBSyncBar
                      addedCount={kbAdded}
                      failedCount={kbFailed}
                      addedExpanded={expandedPanel[log.id] === "succeeded"}
                      failedExpanded={expandedPanel[log.id] === "failed"}
                      onToggleAdded={() => togglePanel(log.id, "succeeded")}
                      onToggleFailed={() => togglePanel(log.id, "failed")}
                      hideSuccess={filterStatus === "hasFailures"}
                      t={tr}
                    />

                    {/* Succeeded 汇总展开面板 */}
                    {expandedPanel[log.id] === "succeeded" && kbAdded > 0 && (
                      <div className="mt-2 rounded-lg border border-green-200 overflow-hidden bg-green-50/50">
                        <div className="flex items-center justify-between px-4 py-2 bg-green-100 border-b border-green-200">
                          <span className="text-xs font-semibold text-green-800">
                            Succeeded files
                          </span>
                          <span className="text-xs font-medium text-green-800">
                            {kbAdded} files
                          </span>
                        </div>
                        <div className="px-4 py-2 bg-white">
                          {(() => {
                            // 三个桶各自已携带正确的逐行 tag / path,直接复用汇总用的成功条目
                            const items = kbSuccessItems;
                            return (
                              <>
                                <div className="divide-y divide-green-100">
                                  {items.slice(0, 5).map((item, idx) => (
                                    <SourceFileRow
                                      key={idx}
                                      index={idx}
                                      name={item.name}
                                      path={item.path}
                                      tag={item.tag}
                                    />
                                  ))}
                                </div>
                                {kbAdded > 5 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      openFileModal({
                                        title: `Succeeded — ${kbAdded} files`,
                                        files: items.map((i) => i.name),
                                        fileItems: items,
                                      })
                                    }
                                    className="mt-2 text-xs font-medium text-[#830051] hover:underline cursor-pointer"
                                  >
                                    View all {kbAdded}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Failed 汇总展开面板 */}
                    {expandedPanel[log.id] === "failed" && kbFailed > 0 && (
                      <div className="mt-2 rounded-lg border border-red-200 overflow-hidden bg-red-50/50">
                        <div className="flex items-center justify-between px-4 py-2 bg-red-100 border-b border-red-200">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-red-800">Failed files</span>
                            {canMarkReviewed && <ReviewHelpPopover />}
                          </div>
                          <span className="text-xs font-medium text-red-800">{kbFailed} files</span>
                        </div>
                        <div className="px-4 py-2 bg-white">
                          <div className="divide-y divide-red-100">
                            {failedFileResults.slice(0, 5).map((item, idx) => {
                              const key = item.markKey!;
                              const isMarked = markedItems.has(key);
                              return (
                                <div
                                  key={idx}
                                  className={`flex items-start gap-3 py-1.5 ${isMarked ? "opacity-50" : ""}`}
                                >
                                  <div className="flex items-start gap-3 flex-shrink-0">
                                    {canMarkReviewed && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          isMarked ? handleUnmark(key) : handleToggleMark(key)
                                        }
                                        className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                                      >
                                        <i
                                          className={`${
                                            isMarked
                                              ? "ri-checkbox-fill text-[#830051]"
                                              : "ri-checkbox-blank-line text-[#830051]"
                                          } text-base`}
                                        ></i>
                                      </button>
                                    )}
                                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-medium text-gray-400">
                                      {idx + 1}
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span
                                      className={`text-sm ${isMarked ? "text-gray-400" : "text-gray-700"}`}
                                    >
                                      {item.file}
                                    </span>
                                    <FileTagBadge tag={item.tag} className="ml-2" />
                                    <p
                                      className={`text-xs mt-0.5 leading-snug ${isMarked ? "text-gray-400" : "text-red-500"}`}
                                    >
                                      {item.reason}
                                    </p>
                                  </div>
                                  <OpenSourceButton path={item.path} />
                                </div>
                              );
                            })}
                          </div>
                          {kbFailed > 5 && (
                            <button
                              type="button"
                              onClick={() =>
                                openFileModal({
                                  title: `Failed — ${kbFailed} files`,
                                  files: [],
                                  failedFiles: failedFileResults,
                                })
                              }
                              className="mt-2 text-xs font-medium text-[#830051] hover:underline cursor-pointer"
                            >
                              View all {kbFailed}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Deleted 展开面板 */}
                    {expandedPanel[log.id] === "deleted" && hasDeleted && (
                      <div
                        className="mt-3 rounded-lg border overflow-hidden"
                        style={{ borderColor: "#fbcfe8", backgroundColor: "#fdf2f8" }}
                      >
                        <div
                          className="flex items-center justify-between px-4 py-2.5 border-b"
                          style={{ backgroundColor: "#fce7f3", borderColor: "#fbcfe8" }}
                        >
                          <div className="flex items-center gap-2">
                            <i
                              className="ri-delete-bin-line text-sm"
                              style={{ color: "#830051" }}
                            ></i>
                            <span className="text-xs font-semibold" style={{ color: "#830051" }}>
                              Deleted files
                            </span>
                          </div>
                          <span className="text-xs font-medium" style={{ color: "#830051" }}>
                            {deletedTotal} {t("uploadLogs.files")}
                          </span>
                        </div>
                        {filterStatus !== "hasFailures" && deletedSuccess > 0 && (
                          <div className="px-4 py-2 bg-white">
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="text-xs font-medium text-green-700">
                                {t("uploadLogs.succeeded")} ({deletedSuccess})
                              </div>
                              {deletedSuccess > 5 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    openFileModal({
                                      title: `${t("uploadLogs.succeeded")} — ${deletedSuccess} ${t("uploadLogs.files")}`,
                                      files: deletedSuccessFiles.map((i) => i.name),
                                      fileItems: deletedSuccessFiles,
                                    })
                                  }
                                  className="text-xs font-medium text-[#830051] hover:underline whitespace-nowrap cursor-pointer"
                                >
                                  {t("uploadLogs.viewAll")} {deletedSuccess}
                                </button>
                              )}
                            </div>
                            <div className="divide-y divide-green-100">
                              {deletedSuccessFiles.slice(0, 5).map((file, idx) => (
                                <SourceFileRow
                                  key={idx}
                                  index={idx}
                                  name={file.name}
                                  path={file.path}
                                  textClassName="text-gray-500"
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {deletedFailed > 0 && (
                          <div className="px-4 py-2" style={{ backgroundColor: "#fef2f2" }}>
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-2">
                                <div className="text-xs font-medium text-red-700">
                                  {t("uploadLogs.failedFiles")} ({deletedFailed})
                                </div>
                                {canMarkReviewed && <ReviewHelpPopover />}
                              </div>
                              {deletedFailed > 5 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    openFileModal({
                                      title: `${t("uploadLogs.failedFiles")} — ${deletedFailed} ${t("uploadLogs.files")}`,
                                      files: [],
                                      failedFiles: deletedFailedFileResults,
                                    })
                                  }
                                  className="text-xs font-medium text-[#830051] hover:underline whitespace-nowrap cursor-pointer"
                                >
                                  {t("uploadLogs.viewAll")} {deletedFailed}
                                </button>
                              )}
                            </div>
                            <div className="divide-y divide-red-100">
                              {deletedFailedFileResults.slice(0, 5).map((item, idx) => {
                                const key = item.markKey ?? `${log.id}-deleted-${item.file}`;
                                const isMarked = markedItems.has(key);
                                return (
                                  <div
                                    key={idx}
                                    className={`flex items-start gap-3 py-1.5 ${isMarked ? "opacity-50" : ""}`}
                                  >
                                    <div className="flex items-start gap-3 flex-shrink-0">
                                      {canMarkReviewed && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            isMarked ? handleUnmark(key) : handleToggleMark(key)
                                          }
                                          className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
                                        >
                                          <i
                                            className={`${
                                              isMarked
                                                ? "ri-checkbox-fill text-[#830051]"
                                                : "ri-checkbox-blank-line text-[#830051]"
                                            } text-base`}
                                          ></i>
                                        </button>
                                      )}
                                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-medium text-gray-400">
                                        {idx + 1}
                                      </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <span
                                        className={`text-sm ${isMarked ? "text-gray-400" : "text-gray-700"}`}
                                      >
                                        {item.file}
                                      </span>
                                      <p
                                        className={`text-xs mt-0.5 leading-snug ${isMarked ? "text-gray-400" : "text-red-500"}`}
                                      >
                                        {item.reason}
                                      </p>
                                    </div>
                                    <OpenSourceButton path={item.path} />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* New 展开面板 */}
                    {expandedPanel[log.id] === "new" && hasNew && (
                      <OperationPanel
                        title="New files"
                        count={newTotal}
                        successCount={newSuccess}
                        failedCount={newFailed}
                        successFiles={addedSuccessFiles}
                        failedFiles={newFailedFileResults}
                        theme="pink"
                        t={tr}
                        hideSuccess={filterStatus === "hasFailures"}
                        markKeyPrefix={`${log.id}-failed`}
                        markedItems={markedItems}
                        onToggleMark={handleToggleMark}
                        onUnmark={handleUnmark}
                        canMarkReviewed={canMarkReviewed}
                        onViewAllSuccess={() =>
                          openFileModal({
                            title: `${t("uploadLogs.succeeded")} — ${newSuccess} ${t("uploadLogs.files")}`,
                            files: addedSuccessFiles.map((i) => i.name),
                            fileItems: addedSuccessFiles,
                          })
                        }
                        onViewAllFailed={() =>
                          openFileModal({
                            title: `${t("uploadLogs.failedFiles")} — ${newFailed} ${t("uploadLogs.files")}`,
                            files: [],
                            failedFiles: newFailedFileResults,
                          })
                        }
                      />
                    )}

                    {/* Retry 展开面板 */}
                    {expandedPanel[log.id] === "retry" && hasRetry && (
                      <OperationPanel
                        title="Retried files"
                        count={retryTotal}
                        successCount={retrySuccess}
                        failedCount={retryFailed}
                        successFiles={retriedSuccessFiles}
                        failedFiles={retryFailedFileResults}
                        theme="slate"
                        t={tr}
                        hideSuccess={filterStatus === "hasFailures"}
                        markKeyPrefix={`${log.id}-retried`}
                        markedItems={markedItems}
                        onToggleMark={handleToggleMark}
                        onUnmark={handleUnmark}
                        canMarkReviewed={canMarkReviewed}
                        onViewAllSuccess={() =>
                          openFileModal({
                            title: `${t("uploadLogs.succeeded")} — ${retrySuccess} ${t("uploadLogs.files")}`,
                            files: retriedSuccessFiles.map((i) => i.name),
                            fileItems: retriedSuccessFiles,
                          })
                        }
                        onViewAllFailed={() =>
                          openFileModal({
                            title: `${t("uploadLogs.failedFiles")} — ${retryFailed} ${t("uploadLogs.files")}`,
                            files: [],
                            failedFiles: retryFailedFileResults,
                          })
                        }
                      />
                    )}

                    {/* Modified 展开面板 */}
                    {expandedPanel[log.id] === "modified" && hasModified && (
                      <OperationPanel
                        title="Modified files"
                        count={modifiedTotal}
                        successCount={modifiedSuccess}
                        failedCount={modifiedFailed}
                        successFiles={updatedSuccessFiles}
                        failedFiles={modifiedFailedFileResults}
                        theme="slate"
                        t={tr}
                        hideSuccess={filterStatus === "hasFailures"}
                        markKeyPrefix={`${log.id}-updated`}
                        markedItems={markedItems}
                        onToggleMark={handleToggleMark}
                        onUnmark={handleUnmark}
                        canMarkReviewed={canMarkReviewed}
                        onViewAllSuccess={() =>
                          openFileModal({
                            title: `${t("uploadLogs.succeeded")} — ${modifiedSuccess} ${t("uploadLogs.files")}`,
                            files: updatedSuccessFiles.map((i) => i.name),
                            fileItems: updatedSuccessFiles,
                          })
                        }
                        onViewAllFailed={() =>
                          openFileModal({
                            title: `${t("uploadLogs.failedFiles")} — ${modifiedFailed} ${t("uploadLogs.files")}`,
                            files: [],
                            failedFiles: modifiedFailedFileResults,
                          })
                        }
                      />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <AdminPagination
          total={total}
          page={page}
          pageSize={PAGE_SIZE}
          loading={loading}
          onPageChange={(nextPage) => setPage(nextPage)}
        />
      </div>

      <FileListModal
        open={modalState.open}
        onClose={closeFileModal}
        title={modalState.title}
        files={modalState.files}
        failedFiles={modalState.failedFiles}
        fileItems={modalState.fileItems}
        markedItems={markedItems}
        onToggleMark={handleToggleMark}
        onUnmark={handleUnmark}
        canMarkReviewed={canMarkReviewed}
      />
    </AdminLayout>
  );
}
